import {
  Column,
  EnquiryHandle,
  IServerSideGetRowsParams,
  ServerSideCriteria,
  combineFunctions,
  useServerEnquiry,
} from '@access-erp/common';
import { Guid } from 'guid-typescript';
import { useEffect, useRef, useState } from 'react';

import { ContractsEnquiriesScreen } from '@/components/common/EnquiriesScreen/providers/ContractsEnquiriesScreen';
import { CONTRACTS_PER_PAGE } from '@/config/common.config';
import { UserRole } from '@/config/roles.config';
import { Forms } from '@/graphql/contracts/_generated_/contracts';
import useGetProducts, { PaginatedFilteredProducts } from './hooks/useGetProducts';
import { useGetInitialFiltersSet } from '@/components/common/EnquiriesScreen/hooks/useGetInitialFiltersSet';
import { GetPaginatedFilteredProductsVariables }
  from '@/graphql/contracts/_generated_/getPaginatedFilteredProducts.query';
import { replaceAdvancedValueType } from '@/components/common/EnquiriesScreen/utils/advancedFilterHelpers';
import { useHeaderColumns } from './columns/useHeaderColumns';
import { getSavedSearch } from '@/components/common/EnquiriesScreen/utils/savedSearch';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { ProductTypeWithId } from '@/utils/data-helpers/types';
import useProductsNavigation from './hooks/useProductsNavigation';
import { useRightPanel } from './hooks/useRightPanel';
import { BillingTypeModal } from './components/BillingTypeModal/BillingTypeModal';
import { EvoProductMergeDefaultsModal } from './components/ProductMergeDefaults';

const ProductsComponent = (): React.JSX.Element => {
  const [pageIsLoading, setPageIsLoading] = useState(true);
  const gridRef = useRef<EnquiryHandle<ProductTypeWithId>>(null);
  const { headerColumns } = useHeaderColumns();
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();
  const { goToProductsBySearch } = useProductsNavigation();
  const { getProducts } = useGetProducts();
  const { getSavedFilterIfAny, savedFilters } = useGetInitialFiltersSet();
  const enquiry = useServerEnquiry<
  ProductTypeWithId,
  GetPaginatedFilteredProductsVariables['filter']
  >({
    gridRef,
    headerColumns: headerColumns as Column<ProductTypeWithId>[],
    initialFilter: replaceAdvancedValueType(savedFilters),
  });

  const {
    tabsConfiguration,
    closeBillingTypeModal,
    isVisibleBillingTypeModal,
    closeProductMergeDefaultsModal,
    isVisibleProductMergeDefaultsModal,
  } = useRightPanel({
    selectedHeaderRows: enquiry.selectedHeaderRows,
    userRole,
  });

  useEffect(() => {
    const getInitialData = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };

    getInitialData();
  }, [getUserRole]);

  const searchTextFromQuery = getSavedSearch?.()?.savedSearch;
  enquiry.refreshSearch = combineFunctions(enquiry.refreshSearch);

  const getData = async (
    params: IServerSideGetRowsParams,
    serverSideCriteria: ServerSideCriteria,
  ) => {
    await getSavedFilterIfAny(Forms.Contracts, params);

    const dto: PaginatedFilteredProducts = {
      after: serverSideCriteria.nextPageCursor,
      first: CONTRACTS_PER_PAGE,
      filter: {
        summary: enquiry.serverSide?.current?.search ?? searchTextFromQuery,
        ...(enquiry.serverSide?.current?.customFilters ?? {}),
        ...serverSideCriteria.groupingFilters,
      },
      sort: serverSideCriteria.userSetSorting,
      group: serverSideCriteria.groupByColumn as any,
    };

    const data = await getProducts(dto);

    setPageIsLoading(false);
    const searchIsCleared = enquiry.serverSide?.current?.search === '';
    if (enquiry.serverSide?.current?.search || searchIsCleared) {
      goToProductsBySearch(enquiry.serverSide?.current?.search);
    }
    return {
      items:
                data?.items?.map((el) => ({
                  ...el,
                  id: el?.iD ?? Guid.create().toString(),
                })) ?? [],
      pageInfo: {
        hasNextPage: data?.pageInfo.hasNextPage,
        hasPreviousPage: data?.pageInfo.hasPreviousPage,
        endCursor: data?.pageInfo?.endCursor,
        startCursor: data?.pageInfo?.startCursor,
      },
    };
  };

  const refreshGrid = () => {
    gridRef.current?.deselectRows();
    gridRef.current?.refreshServerSideGrid({
      purge: true,
    });
  };


  return (
    <>
      <ContractsEnquiriesScreen
        ref={gridRef}
        enquiry={enquiry}
        filterForm={Forms.Products}
        formName={Forms.Products}
        headerProps={{
          primaryTitle: 'Products',
          secondaryTitle: 'List',
          searchPlaceholder: 'Start typing product name',
          search: searchTextFromQuery,
          showExportAll: userRole === UserRole.FULL_ACCESS || userRole === UserRole.ADMIN,
          showExportCurrent: false,
        }}
        detailProps={{
          exportFileName: 'Products',
          rowGroupPanelShow: 'always',
          getData: getData as any,
          pageSize: CONTRACTS_PER_PAGE,
          suppressRowClickSelection: false,
          loading: pageIsLoading,
        }}
        rightPanelTabs={tabsConfiguration}
      />
      {isVisibleBillingTypeModal && enquiry.selectedHeaderRows[0]?.data && (
        <BillingTypeModal
          refreshGrid={refreshGrid}
          selectedRow={enquiry.selectedHeaderRows[0].data}
          onClose={closeBillingTypeModal}
          isVisible={isVisibleBillingTypeModal}
        />
      )}
      <EvoProductMergeDefaultsModal
        open={isVisibleProductMergeDefaultsModal}
        onOpenChange={(open) => {
          if (!open) closeProductMergeDefaultsModal();
        }}
        refreshGrid={refreshGrid}
      />
    </>
  );
};

export default ProductsComponent;
